<template>
  <v-container fluid>
    <!-- Header -->
    <v-row align="center" class="mt-n4">
      <v-col cols="12" md="auto">
        <div class="d-flex align-center">
          <v-btn icon class="mr-2" @click="goBack">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-avatar color="primary" size="48" class="mr-3 elevation-2">
            <v-icon color="white" size="26">mdi-cog-play</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold grey--text text--darken-2">
              Procesar prestaciones - Grupo #{{ benefit_group_id }}
            </div>
            <div class="text-caption grey--text">
              {{ doneCount }} de {{ employees.length }} facturas generadas
            </div>
          </div>
        </div>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" md="auto" class="d-flex align-center" style="gap: 12px; flex-wrap: wrap;">
        <v-autocomplete v-model="emisorBusiness" :items="emisorBusinessItems" label="Emisor - Unidad de negocio"
          color="primary" item-color="primary" outlined dense hide-details class="emisor-input"
          @change="onEmisorBusinessChange">
          <template v-slot:prepend-inner>
            <v-icon color="primary" size="20">mdi-office-building-outline</v-icon>
          </template>
        </v-autocomplete>
        <v-autocomplete v-model="emisorLocation" :items="emisorLocationItems" label="Emisor - Sucursal"
          return-object color="primary" item-color="primary" outlined dense hide-details class="emisor-input">
          <template v-slot:prepend-inner>
            <v-icon color="primary" size="20">mdi-map-marker-outline</v-icon>
          </template>
        </v-autocomplete>
        <v-btn color="success" depressed class="text-none" :loading="processingAll"
          :disabled="pendingEmployees.length == 0 || loading" @click="generateAll">
          <v-icon left>mdi-cogs</v-icon>
          Generar todas ({{ pendingEmployees.length }})
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading" justify="center">
      <v-col cols="12" md="10">
        <v-skeleton-loader type="article, table"></v-skeleton-loader>
      </v-col>
    </v-row>

    <v-stepper v-else v-model="currentStep" class="elevation-1 rounded-lg mt-2">
      <v-stepper-header>
        <template v-for="(o, i) in visibleSteps">
          <v-stepper-step :key="`step-${o.emp.employee_id}`" :step="o.index + 1" editable
            :complete="o.emp.status === 'done'" :color="stepColor(o.emp)" :rules="[() => o.emp.status !== 'error']">
            <div class="d-flex align-center" style="gap: 6px;">
              <span class="font-weight-bold">{{ o.emp.employee_fullname }}</span>
              <v-chip v-if="o.emp.status === 'done'" x-small color="success" dark>
                <v-icon left x-small>mdi-check</v-icon>{{ o.emp.invoice_number }}
              </v-chip>
              <v-chip v-else-if="o.emp.status === 'error'" x-small color="error" dark>
                <v-icon left x-small>mdi-alert</v-icon>Error
              </v-chip>
              <v-chip v-else-if="o.emp.status === 'processing'" x-small color="primary" dark>
                <v-icon left x-small>mdi-loading mdi-spin</v-icon>Procesando
              </v-chip>
              <v-chip v-else-if="!o.emp.is_supplier" x-small color="error" outlined>
                <v-icon left x-small>mdi-account-alert</v-icon>No suplidor
              </v-chip>
            </div>
            <small class="grey--text">
              Empleado {{ o.index + 1 }} de {{ employees.length }} · Neto:
              {{ currencyFormat(o.emp.totalOrderAmount) }} {{ o.emp.currency_code }}
            </small>
          </v-stepper-step>
          <v-divider v-if="i + 1 < visibleSteps.length" :key="`div-${o.emp.employee_id}`"></v-divider>
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <template v-for="(emp, index) in employees">
        <v-stepper-content :key="`content-${emp.employee_id}`" :step="index + 1">
          <!-- Invoice-like preview -->
          <v-card outlined class="rounded-lg mb-3">
            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="text-overline grey--text">Suplidor (empleado)</div>
                  <div class="font-weight-bold">{{ emp.entity_name || emp.employee_fullname }}</div>
                  <div class="text-caption grey--text">Cédula: {{ emp.dni }}</div>
                  <v-chip v-if="emp.is_supplier" x-small color="success" outlined class="mt-1">
                    <v-icon left x-small>mdi-check-decagram</v-icon>Registrado como suplidor
                  </v-chip>
                  <v-chip v-else x-small color="error" outlined class="mt-1">
                    <v-icon left x-small>mdi-account-alert</v-icon>No registrado como suplidor
                  </v-chip>
                </v-col>
                <v-col cols="12" md="6" class="text-md-right">
                  <div class="text-overline grey--text">Documento interno por pagar</div>
                  <div class="text-caption font-weight-bold">Referencia: {{ emp.supplier_invoice_number }}</div>
                  <div class="text-caption">Fecha: {{ formatDate(emp.date_out) }}</div>
                  <div class="text-caption">Departamento: {{ emp.department_id || 'N/D' }}</div>
                  <div class="text-caption">
                    Moneda: <strong>{{ emp.currency_code }}</strong>
                    <span v-if="emp.currency_value"> · Tasa a USD: {{ emp.currency_rate.toFixed(6) }}</span>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Artículo</th>
                      <th class="text-left">Tipo</th>
                      <th class="text-left">Concepto</th>
                      <th class="text-left">Referencia</th>
                      <th class="text-right">Cantidad</th>
                      <th class="text-right">Precio unit.</th>
                      <th class="text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(it, i) in emp.items" :key="i">
                      <td>{{ it.item_id }}</td>
                      <td>
                        <v-chip x-small label :color="it._kind === 'accrual' ? 'green lighten-4' : 'red lighten-4'">
                          {{ it._kind === 'accrual' ? 'Devengo' : 'Deducción' }}
                        </v-chip>
                      </td>
                      <td>{{ it.item_description }}</td>
                      <td>{{ it.reference || '-' }}</td>
                      <td class="text-right">{{ it.quantity }}</td>
                      <td class="text-right">{{ currencyFormat(Math.abs(it.price)) }}</td>
                      <td class="text-right" :class="it.price < 0 ? 'red--text' : ''">
                        {{ currencyFormat(Math.abs(it.price * it.quantity)) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-divider class="my-2"></v-divider>
              <v-row dense class="text-right">
                <v-col cols="12" md="8" class="text-md-right grey--text">Devengos</v-col>
                <v-col cols="12" md="4" class="text-right">{{ currencyFormat(emp.totalAccrual) }}</v-col>
                <v-col cols="12" md="8" class="text-md-right grey--text">Deducciones</v-col>
                <v-col cols="12" md="4" class="text-right red--text">-{{ currencyFormat(emp.totalDeduction) }}</v-col>
                <v-col cols="12" md="8" class="text-md-right font-weight-bold">Neto a pagar</v-col>
                <v-col cols="12" md="4" class="text-right font-weight-bold">
                  {{ currencyFormat(emp.totalOrderAmount) }} {{ emp.currency_code }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Accounting (reused PreAccounting) -->
          <PreAccounting v-if="currentStep === index + 1 && emp.order_item_entries.length > 0"
            :key="`pa-${emp.employee_id}`" :invoice_type="4" :cant_edit="true" :search_names="true"
            :order_item_entries="emp.order_item_entries" :totalOrderAmount="emp.totalOrderAmount" :show_credits="false"
            :cuadre_in_init="true" :business_unit_id="emp.business_unit_id"
            @set_accounts="(accounts) => onAccountsSaved(emp, accounts)"
            @emit_edited_accounts="(accounts) => onAccountsEdited(emp, accounts)" />

          <v-alert v-if="!emp.is_supplier" type="warning" dense text border="left" class="mt-3">
            <strong>{{ emp.entity_name || emp.employee_fullname }}</strong> no está registrado como suplidor en
            Entidades. Debes habilitarlo como suplidor antes de poder generar la factura.
          </v-alert>

          <v-alert v-if="emp.status === 'error'" type="error" dense text border="left" class="mt-3">
            {{ emp.error_message }}
          </v-alert>

          <div class="d-flex align-center mt-3" style="gap: 8px;">
            <v-btn v-if="emp.status !== 'done'" color="primary" depressed class="text-none"
              :loading="emp.status === 'processing'" :disabled="!emp.is_supplier" @click="generateOne(emp)">
              <v-icon left>mdi-content-save</v-icon>
              Generar factura
            </v-btn>
            <v-btn v-else text color="primary" class="text-none" @click="showInvoice(emp.invoice_id, true)">
              <v-icon left>mdi-file-eye</v-icon>
              Ver factura {{ emp.invoice_number }}
            </v-btn>
            <v-btn v-if="index + 1 < employees.length" text class="text-none" @click="currentStep = index + 2">
              Siguiente
            </v-btn>
          </div>
        </v-stepper-content>
        </template>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import moment from "moment";
import PreAccounting from "@/components/Accounting/PreAccounting.vue";

export default {
  name: "BenefitProcess",
  components: { PreAccounting },
  data() {
    return {
      loading: false,
      processingAll: false,
      currentStep: 1,
      employees: [],
      emisorBusiness: null,
      emisorLocation: null,
      emisorBusinessItems: [],
      emisorLocationItems: [],
    };
  },
  computed: {
    benefit_group_id() {
      return parseInt(this.$route.params.benefit_group_id);
    },
    requestedEmployeeIds() {
      const q = this.$route.query.employee_ids;
      if (!q) return [];
      return String(q)
        .split(",")
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
    },
    visibleSteps() {
      // Solo anterior, actual y siguiente para no saturar el timeline con N empleados.
      return this.employees
        .map((emp, index) => ({ emp, index }))
        .filter((o) => Math.abs(o.index + 1 - this.currentStep) <= 1);
    },
    pendingEmployees() {
      return this.employees.filter((e) => e.status !== "done");
    },
    doneCount() {
      return this.employees.filter((e) => e.status === "done").length;
    },
    userId() {
      return this.$store.state.auth.user.user.user_id;
    },
  },
  created() {
    this.initEmisorData();
    this.init();
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "-";
    },
    stepColor(emp) {
      if (emp.status === "done") return "success";
      if (emp.status === "error") return "error";
      return "primary";
    },
    goBack() {
      this.$router.push({
        name: "BenefitGroupDetail",
        params: { benefit_group_id: this.benefit_group_id },
      });
    },
    async init() {
      this.loading = true;
      try {
        const response = await this.api_Post("humanResource/benefit_group_employees", {
          benefit_group_id: this.benefit_group_id,
        });
        const all = await response.json();
        const ids = this.requestedEmployeeIds;

        const selected = (Array.isArray(all) ? all : []).filter((e) => {
          if (e.invoice_id) return false; // ya procesado
          if (ids.length === 0) return true; // sin selección = todos los pendientes
          return ids.includes(parseInt(e.employee_id));
        });

        if (selected.length === 0) {
          this.$toast.info("No hay empleados pendientes por procesar");
          this.loading = false;
          return;
        }

        const detailPromises = selected.map((emp) =>
          this.api_Post("humanResource/benefit_employee_details", {
            benefit_group_id: this.benefit_group_id,
            employee_id: emp.employee_id,
          }).then((r) => r.json())
        );
        const details = await Promise.all(detailPromises);

        this.employees = selected.map((emp, i) =>
          this.buildEmployee(emp, details[i].accruals || [], details[i].deductions || [])
        );
      } catch (error) {
        console.error(error);
        this.$toast.error("Error al cargar el detalle de los empleados");
      } finally {
        this.loading = false;
      }
    },
    buildEmployee(emp, accruals, deductions) {
      const items = [];
      const orderItemEntries = [];
      const accountsSaved = [];
      let lineIndex = 0;
      let totalAccrual = 0;
      let totalDeduction = 0;
      const department_id = emp.department_id || 0;
      const location_id = emp.location_id;
      const category_id = emp.diary_book_category_id || null;
      const subcategory_id = emp.diary_book_subcategory_id || null;
      const currency_code = emp.currency_code || "DOP";
      // value = unidades de la moneda por 1 USD (base). El invoice guarda 1/value (USD por unidad).
      // Si la moneda es la base de la unidad, value viene null y el rate es 1.
      const currency_value = parseFloat(emp.currency_value) || null;
      const currency_rate =
        currency_value && currency_value > 0 ? 1 / currency_value : 1;

      // Devengos -> débito (positivo)
      for (const acc of accruals) {
        const amount = parseFloat(acc.amount_paid) || 0;
        if (amount === 0) continue;
        totalAccrual += amount;
        const entry_id = `acc_${lineIndex++}`;
        const line = {
          entry_id,
          item_id: acc.item_id,
          chart_of_account_id: acc.debit_chart_of_account_id,
          entry_type_id: 1,
          location_id,
          department_id,
          category_id,
          subcategory_id,
          amount,
        };
        accountsSaved.push(line);
        const total_days = parseFloat(acc.total_days) || 1;
        items.push({
          _kind: "accrual",
          entry_id,
          item_id: acc.item_id,
          item_description: acc.description_long || acc.item_name,
          reference: null,
          quantity: total_days,
          price: parseFloat((amount / total_days).toFixed(6)),
          chart_of_account_id: acc.debit_chart_of_account_id,
        });
        orderItemEntries.push({
          entry_id,
          item_id: acc.item_id,
          item_name: acc.description_long || acc.item_name,
          currency_code,
          amount: amount,
          order_chart_of_accounts: [
            {
              chart_of_account_id: acc.debit_chart_of_account_id,
              chart_of_account_code: acc.debit_account_code,
              chart_of_account_name: acc.debit_account_name,
              category_id,
              subcategory_id,
              location_id,
              department_id,
              entry_type_id: 1,
              amount,
            },
          ],
        });
      }

      // Deducciones -> crédito (negativo en el detalle)
      for (const ded of deductions) {
        const amount = parseFloat(ded.amount_paid) || 0;
        if (amount === 0) continue;
        totalDeduction += amount;
        const entry_id = `ded_${lineIndex++}`;
        const ded_category_id = ded.category_id || null;
        const ded_subcategory_id = ded.debit_account_sub_category_id || null;
        const line = {
          entry_id,
          item_id: ded.item_id,
          chart_of_account_id: ded.credit_chart_of_account_id,
          entry_type_id: 2,
          location_id,
          department_id: 0,
          category_id: ded_category_id,
          subcategory_id: ded_subcategory_id,
          amount,
        };
        accountsSaved.push(line);
        items.push({
          _kind: "deduction",
          entry_id,
          item_id: ded.item_id,
          item_description: ded.description_long || ded.item_name,
          reference: ded.invoice_number || null,
          quantity: 1,
          price: -amount,
          chart_of_account_id: ded.credit_chart_of_account_id,
        });
        orderItemEntries.push({
          entry_id,
          item_id: ded.item_id,
          item_name: ded.description_long || ded.item_name,
          currency_code,
          amount: -amount,
          order_chart_of_accounts: [
            {
              chart_of_account_id: ded.credit_chart_of_account_id,
              chart_of_account_code: ded.credit_account_code,
              chart_of_account_name: ded.credit_account_name,
              category_id: ded_category_id,
              subcategory_id: ded_subcategory_id,
              location_id,
              department_id: 0,
              entry_type_id: 2,
              amount,
            },
          ],
        });
      }

      const net = parseFloat((totalAccrual - totalDeduction).toFixed(2));

      return {
        employee_id: emp.employee_id,
        employee_code: emp.employee_code,
        supplier_invoice_number: `${this.benefit_group_id}-${emp.employee_code}`,
        employee_fullname: emp.employee_fullname,
        dni: emp.dni,
        entity_id: emp.entity_id,
        entity_name: emp.entity_name,
        is_supplier: emp.is_supplier === true,
        employee_category_id: emp.employee_category_id || null,
        diary_book_category_id: emp.diary_book_category_id || null,
        diary_book_subcategory_id: emp.diary_book_subcategory_id || null,
        currency_id: parseInt(emp.currency_id) || 1,
        currency_code,
        currency_value,
        currency_rate,
        date_out: emp.date_out,
        location_id: emp.location_id,
        business_unit_id: parseInt(emp.business_unit_id),
        department_id: emp.department_id,
        items,
        order_item_entries: orderItemEntries,
        accountsSaved,
        totalAccrual,
        totalDeduction,
        totalOrderAmount: net,
        status: "pending",
        invoice_id: null,
        invoice_number: null,
        error_message: null,
      };
    },
    onAccountsSaved(emp, accounts) {
      // Reemplaza la contabilidad con lo que el usuario cuadró/editó en PreAccounting.
      emp.accountsSaved = accounts.map((a) => ({
        entry_id: a.entry_id,
        item_id: a.item_id,
        chart_of_account_id: a.chart_of_account_id,
        entry_type_id: a.entry_type_id,
        location_id: a.location_id,
        department_id: a.department_id,
        category_id: a.category_id || null,
        subcategory_id: a.subcategory_id || null,
        amount: parseFloat(a.amount),
      }));
    },
    onAccountsEdited() {
      // hook reservado para detectar cambios sin guardar (no se usa por ahora)
    },
    buildItems(emp) {
      return emp.items.map((it) => {
        // Empareja por entry_id (único por línea); fallback a item_id.
        let accounts = emp.accountsSaved.filter((a) => a.entry_id === it.entry_id);
        if (accounts.length === 0) {
          accounts = emp.accountsSaved.filter((a) => String(a.item_id) === String(it.item_id));
        }
        return {
          withholding_id: null,
          withholding_amount: 0,
          item_id: it.item_id,
          order_entry_id: null,
          item_description: it.item_description,
          quantity: it.quantity,
          tax_id: 1,
          price: it.price,
          warehouse_id: null,
          item_chart_of_account_id: it.chart_of_account_id,
          discount: 0,
          cost: 0,
          unit_id: 38,
          accounts: accounts.map((a) => ({
            chart_of_account_id: a.chart_of_account_id,
            entry_type_id: a.entry_type_id,
            account_type_id: a.entry_type_id,
            location_id: a.location_id,
            dgii_category_id: null,
            department_id: a.department_id,
            category_id: a.category_id,
            subcategory_id: a.subcategory_id,
            amount: parseFloat(a.amount),
          })),
          note: it.item_description,
          is_inventoriable: false,
        };
      });
    },
    initEmisorData() {
      // Populate business units from the store (same source as SearchHeaderPO)
      const units = this.$store.state.auth.bussines_units || [];
      this.emisorBusinessItems = units.map(u => ({ text: u.text, value: u.value }));

      // Default to the currently selected business in the filter
      const storeBusiness = this.$store.state.app.filters.business;
      this.emisorBusiness = storeBusiness || (units.length > 0 ? units[0].value : null);

      // Populate locations filtered by the selected emisor business
      this.updateEmisorLocations();

      // Default to the currently selected location in the filter
      const storeLocation = this.$store.state.app.filters.location;
      if (storeLocation) {
        const locValue = typeof storeLocation === 'object' ? storeLocation.value : storeLocation;
        const match = this.emisorLocationItems.find(l => l.value === locValue);
        this.emisorLocation = match || (this.emisorLocationItems.length > 0 ? this.emisorLocationItems[0] : null);
      } else if (this.emisorLocationItems.length > 0) {
        this.emisorLocation = this.emisorLocationItems[0];
      }
    },
    updateEmisorLocations() {
      const allLocations = this.$store.state.auth.locations || [];
      if (this.emisorBusiness && this.emisorBusiness !== 0) {
        this.emisorLocationItems = allLocations
          .filter(loc => loc.bus_id === this.emisorBusiness)
          .map(loc => ({ text: loc.text, value: loc.value }));
      } else {
        this.emisorLocationItems = allLocations.map(loc => ({ text: loc.text, value: loc.value }));
      }
      // Validate current selection still exists
      if (this.emisorLocation) {
        const currentValue = typeof this.emisorLocation === 'object' ? this.emisorLocation.value : this.emisorLocation;
        const exists = this.emisorLocationItems.find(l => l.value === currentValue);
        if (!exists && this.emisorLocationItems.length > 0) {
          this.emisorLocation = this.emisorLocationItems[0];
        }
      }
    },
    onEmisorBusinessChange() {
      this.updateEmisorLocations();
      // Reset location to first available
      this.emisorLocation = this.emisorLocationItems.length > 0 ? this.emisorLocationItems[0] : null;
    },
    buildPayload(emp) {
      const dateOut = emp.date_out ? moment(emp.date_out).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
      const time = moment().format("HH:mm:ss");
      // Use emisor (issuer) business unit and location instead of the employee's own
      const emisorBusinessId = this.emisorBusiness || emp.business_unit_id;
      const emisorLocationId = this.emisorLocation
        ? (typeof this.emisorLocation === 'object' ? this.emisorLocation.value : this.emisorLocation)
        : emp.location_id;
      return {
        in_invoice_id: null,
        in_category_id: 2,
        in_type_id: 4,
        in_voucher_type_id: null,
        in_business_unit_id: emisorBusinessId,
        in_location_id: emisorLocationId,
        in_department_id: emp.department_id || null,
        in_entity_id: emp.entity_id,
        in_responsable_entity_id: null,
        in_entity_branch_id: null,
        in_created_by: this.userId,
        in_effective_date: `${dateOut} ${time}`,
        in_account_date: `${dateOut} ${time}`,
        in_expire_at: dateOut,
        in_currency_id: emp.currency_id,
        in_currency_rate: emp.currency_rate,
        in_note: `Prestaciones ${emp.employee_fullname} - Grupo ${this.benefit_group_id}`,
        in_box_entry_id: null,
        in_order_entry_id: null,
        in_ncf_invoice_id: null,
        in_discount: 0,
        in_tip: 4,
        in_invoice_vouchers: [],
        in_payments: [],
        in_entity_payments: [],
        in_withholdings: [],
        in_supplier_details: {
          supplier_invoice_number: emp.supplier_invoice_number,
          supplier_invoice_voucher: null,
        },
        in_items: this.buildItems(emp),
        is_bussiness: true,
        in_installments: null,
        in_quotation_id: null,
        others_settings: { status_order_id: null, metadata: {} },
      };
    },
    async generateOne(emp) {
      if (emp.status === "done") return true;
      if (!emp.is_supplier) {
        emp.status = "error";
        emp.error_message =
          "El empleado no está registrado como suplidor. Habilítalo en Entidades antes de facturar.";
        this.$toast.error(`${emp.employee_fullname} no está registrado como suplidor`);
        return false;
      }
      emp.status = "processing";
      emp.error_message = null;
      try {
        const payload = this.buildPayload(emp);
        const response = await this.api_Post("income/invoice_register", payload);
        if (response.status !== 200) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || "Error al registrar la factura");
        }
        const result = await response.json();
        const invoice = Array.isArray(result) ? result[0] : result;
        if (!invoice || !invoice.id) {
          throw new Error("La factura no devolvió un identificador válido");
        }

        const saveResponse = await this.api_Post("humanResource/set_benefit_invoice", {
          benefit_group_id: this.benefit_group_id,
          employee_id: emp.employee_id,
          invoice_id: invoice.id,
        });
        if (saveResponse.status !== 200) {
          throw new Error("La factura se creó pero no se pudo asociar al empleado");
        }

        emp.invoice_id = invoice.id;
        emp.invoice_number = invoice.invoice_number;
        emp.status = "done";
        this.$toast.success(`Factura ${invoice.invoice_number} generada para ${emp.employee_fullname}`);
        return true;
      } catch (error) {
        console.error(error);
        emp.status = "error";
        emp.error_message = error.message || "Error desconocido";
        return false;
      }
    },
    async generateAll() {
      this.processingAll = true;
      for (let i = 0; i < this.employees.length; i++) {
        const emp = this.employees[i];
        if (emp.status === "done") continue;
        this.currentStep = i + 1;
        await this.generateOne(emp);
      }
      this.processingAll = false;
      const failed = this.employees.filter((e) => e.status === "error");
      if (failed.length === 0) {
        this.$toast.success("Todas las facturas se generaron correctamente");
      } else {
        this.$toast.warning(`${failed.length} factura(s) fallaron. Puedes reintentarlas.`);
      }
    },
  },
};
</script>

<style scoped>
.emisor-input {
  min-width: 220px;
  max-width: 280px;
}

.emisor-input >>> .v-input__slot {
  min-height: 40px !important;
}

.emisor-input >>> .v-input__prepend-inner {
  margin-top: 8px;
  margin-right: 8px;
}
</style>
